/**
 * MESSAGES PAGE (INBOX)
 *
 * Lists all conversations for the current user, similar to a messaging app inbox.
 *
 * Features:
 * - Real-time conversation list using Firestore subscriptions
 * - Shows last message preview and timestamp
 * - Unread indicator (compares lastMessage timestamp with user's lastReadAt)
 * - Displays other user's avatar and display name
 * - Click conversation to navigate to full chat (/messages/:conversationId)
 * - Sorted by most recent activity (updatedAt field)
 *
 * Data Flow:
 * 1. Subscribe to all conversations where current user is a participant
 * 2. Real-time updates via onSnapshot listener
 * 3. Extract other participant's info from participantInfo map
 * 4. Format relative timestamps (e.g., "5m ago", "2h ago", "3d ago")
 *
 * Empty States:
 * - Not logged in: Shows sign-in prompt
 * - No conversations: Encourages user to browse marketplace
 *
 * Technical Notes:
 * - Uses subscribeToConversations from messagingService.ts
 * - Handles Firestore timestamp conversion
 * - Glassmorphic card styling for modern UI
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { subscribeToConversations, deleteEmptyConversation } from '../messaging/messagingService';
import type { Conversation } from '../messaging/types';
import { MessageSquare, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { AppIcons } from '../constants/icons';

const Messages: React.FC = () => {
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid || null);
      if (!user) {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to conversations
  useEffect(() => {
    if (!currentUserId) return;

    setLoading(true);
    const unsubscribe = subscribeToConversations(currentUserId, (convos) => {
      setConversations(convos);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  // Get other user's info from conversation
  const getOtherUser = (conversation: Conversation) => {
    const otherUserId = conversation.participants.find(id => id !== currentUserId);
    if (!otherUserId) return null;
    return {
      id: otherUserId,
      ...conversation.participantInfo[otherUserId]
    };
  };

  // Format relative time
  const formatRelativeTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Not logged in
  if (!currentUserId && !loading) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        <div className="glass-card p-8 text-center">
          <MessageSquare className="w-16 h-16 text-glass-muted mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-glass mb-2">Messages</h1>
          <p className="font-body text-glass-secondary mb-4">Please sign in to view your messages.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4">
      {/* Header */}
      <div className="glass-card p-6 mb-4">
        <h1 className="font-heading text-3xl text-glass">Messages</h1>
        <p className="font-body text-glass-secondary mt-1">Your conversations with other collectors</p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="glass-card p-12 text-center">
          <Loader2 className="w-12 h-12 text-glass animate-spin mx-auto mb-4" />
          <p className="font-body text-glass-secondary">Loading conversations...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && conversations.length === 0 && (
        <div className="glass-card p-12 text-center">
          <MessageSquare className="w-16 h-16 text-glass-muted mx-auto mb-4" />
          <h2 className="font-heading text-xl text-glass mb-2">No conversations yet</h2>
          <p className="font-body text-glass-secondary mb-4">
            When you message a seller about their items, your conversations will appear here.
          </p>
          <Link
            to="/marketplace"
            className="btn-primary inline-block font-body"
          >
            Browse Marketplace
          </Link>
        </div>
      )}

      {/* Conversation list */}
      {!loading && conversations.length > 0 && (
        <div className="glass-card overflow-hidden">
          {conversations.map((conversation) => {
            const otherUser = getOtherUser(conversation);
            if (!otherUser) return null;

            const isUnread = conversation.lastMessage &&
            conversation.lastMessage.senderId !== currentUserId &&
            (!conversation.lastReadAt?.[currentUserId!] ||
            conversation.lastMessage.timestamp?.toMillis?.() > conversation.lastReadAt[currentUserId!]?.toMillis?.());

            // A conversation is "empty" (a stuck draft) when no message —
            // text OR system — has ever been sent. Clicking "Message
            // seller" spawns a doc immediately; users who back out without
            // typing leave one of these behind. We surface a trash button
            // on hover only for these; anything with real history is
            // protected in the service.
            const isEmptyDraft = !conversation.lastMessage;
            return (
              <div
                key={conversation.id}
                onClick={() => navigate(`/messages/${conversation.id}`)}
                className={`group flex items-center gap-4 p-4 border-b border-slate-600 hover:bg-slate-700/50 cursor-pointer transition-default ${isUnread ? 'bg-slate-700/30' : ''}`}
              >
                {/* Avatar */}
                {otherUser.avatarUrl ? (
                  <img
                    src={otherUser.avatarUrl}
                    alt={otherUser.displayName}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full rainbow-gradient flex items-center justify-center text-white text-xl font-bold">
                    {otherUser.displayName?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={`font-bold truncate font-body ${isUnread ? 'text-glass' : 'text-glass-secondary'}`}>
                      {otherUser.username ? `@${otherUser.username}` : otherUser.displayName}
                    </h3>
                    <span className="text-xs text-glass-muted font-body">
                      {formatRelativeTime(conversation.lastMessage?.timestamp || conversation.updatedAt)}
                    </span>
                  </div>

                  <p className={`text-sm truncate font-body ${isUnread ? 'text-glass font-medium' : 'text-glass-muted'}`}>
                    {conversation.lastMessage?.senderId === currentUserId && 'You: '}
                    {conversation.lastMessage?.text || 'No messages yet'}
                  </p>

                  {/* Pinned items count */}
                  {conversation.pinnedItems && conversation.pinnedItems.length > 0 && (
                    <p className="text-xs text-glass-secondary mt-1 font-body flex items-center gap-1">
                      <AppIcons.Polish className="w-3 h-3 text-current" aria-label="Items" />
                      {conversation.pinnedItems.length} item{conversation.pinnedItems.length !== 1 ? 's' : ''} in discussion
                    </p>
                  )}
                </div>

                {isEmptyDraft && conversation.id && currentUserId ? (
                  <button
                    type="button"
                    aria-label="Delete empty conversation"
                    title="Delete this empty draft"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        !window.confirm(
                          `Delete this empty conversation with ${
                            otherUser.username ? `@${otherUser.username}` : otherUser.displayName
                          }? It has no messages.`
                        )
                      ) {
                        return;
                      }
                      deleteEmptyConversation(conversation.id!, currentUserId).catch((err) => {
                        console.error('delete empty conversation failed', err);
                        window.alert(
                          err?.message ??
                            "Couldn't delete that conversation. Try again in a moment."
                        );
                      });
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition-colors text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete draft</span>
                  </button>
                ) : (
                  <ChevronRight className="icon-md text-glass-muted" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Messages;
